import { useEffect, useRef, useState } from 'react';
import { v } from '../constants';
import { SharedHeader } from './SharedHeader';
import { deleteCustomPuzzle, fetchUserProfile, followUser, unfollowUser, type ProfileFetch } from '../lib/customPuzzles';
import { formatBadgeProgress } from '../lib/badges';
import { useAuth } from '../auth/AuthContext';
import { apiFetch, apiJson } from '../lib/api';

// Public profile page at /u/:displayName. Shows the user's published
// puzzles. Hidden puzzles aren't surfaced (server enforces).

export function UserProfile({ displayName }: { displayName: string }) {
  const auth = useAuth();
  const [profile, setProfile] = useState<ProfileFetch | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Display-name match isn't a perfect ownership signal (names aren't
  // unique yet), but it's good enough to surface the Delete affordance
  // — the server enforces real ownership on DELETE.
  const isOwnProfile = !!auth.user && auth.user.displayName === displayName;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchUserProfile(displayName);
      if (cancelled) return;
      if (!data) { setNotFound(true); setLoading(false); return; }
      setProfile(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [displayName]);

  // Optimistic follow toggle — flip viewerIsFollowing + bump the
  // follower count immediately, then call the API. Roll back the
  // entire patch on failure so the UI never shows a phantom follow.
  const [followBusy, setFollowBusy] = useState(false);
  async function handleFollowToggle() {
    if (!profile || followBusy) return;
    const isFollowing = !!profile.viewerIsFollowing;
    const delta = isFollowing ? -1 : 1;
    const snapshot = profile;
    setProfile({
      ...profile,
      viewerIsFollowing: !isFollowing,
      followerCount: (profile.followerCount ?? 0) + delta,
    });
    setFollowBusy(true);
    const r = isFollowing
      ? await unfollowUser(displayName)
      : await followUser(displayName);
    setFollowBusy(false);
    if (!r.ok) {
      setProfile(snapshot);
      return;
    }
    // Reconcile with the server's canonical counts in case our
    // optimistic delta drifted (e.g. someone else followed/unfollowed
    // between our fetch and our write).
    setProfile(p => p ? {
      ...p,
      viewerIsFollowing: r.viewerIsFollowing ?? p.viewerIsFollowing,
      followerCount: r.followerCount ?? p.followerCount,
      followingCount: r.followingCount ?? p.followingCount,
    } : p);
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete "${name}"? The share link will stop working immediately. This can't be undone.`)) return;
    const r = await deleteCustomPuzzle(id);
    if (r.ok) {
      setProfile(p => p ? { ...p, puzzles: p.puzzles.filter(x => x.id !== id) } : p);
    } else {
      alert(r.error === 'forbidden'
        ? 'You can only delete puzzles you authored.'
        : "Couldn't delete the puzzle. Try again in a moment.");
    }
  }

  // ─── Display-name rename ─────────────────────────────────────────
  // Owner-only inline editor. Same as-you-type availability check the
  // OAuth /complete-signup page uses. Submit posts to
  // POST /auth/me/displayname; on success we refresh AuthContext +
  // hard-nav to the new /u/<name> URL since the page identity is the
  // display name itself.
  type Avail =
    | { kind: 'idle' }
    | { kind: 'checking' }
    | { kind: 'available' }
    | { kind: 'unavailable'; reason: string };
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameAvail, setRenameAvail] = useState<Avail>({ kind: 'idle' });
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  function openRename() {
    setRenameValue(profile?.displayName ?? displayName);
    setRenameAvail({ kind: 'idle' });
    setRenameError(null);
    setRenameOpen(true);
  }

  // Debounced availability check. Skip when the value matches the
  // user's current name (no-op rename — counts as "available").
  useEffect(() => {
    if (!renameOpen) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const trimmed = renameValue.trim();
    const current = (auth.user?.displayName ?? '').toLowerCase();
    if (trimmed.length === 0) { setRenameAvail({ kind: 'idle' }); return; }
    if (trimmed.toLowerCase() === current) { setRenameAvail({ kind: 'available' }); return; }
    setRenameAvail({ kind: 'checking' });
    debounceRef.current = window.setTimeout(async () => {
      const data = await apiJson<{ available: boolean; reason?: string }>(
        `/auth/displayname-available?name=${encodeURIComponent(trimmed)}`,
      );
      if (!data) { setRenameAvail({ kind: 'idle' }); return; }
      if (data.available) setRenameAvail({ kind: 'available' });
      else setRenameAvail({ kind: 'unavailable', reason: data.reason ?? 'in_use' });
    }, 300);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [renameValue, renameOpen, auth.user?.displayName]);

  async function submitRename() {
    setRenameError(null);
    if (renameAvail.kind !== 'available') return;
    setRenameSubmitting(true);
    try {
      const r = await apiFetch('/auth/me/displayname', {
        method: 'POST',
        body: JSON.stringify({ displayName: renameValue.trim() }),
      });
      if (r.ok) {
        const body = await r.json();
        await auth.refresh();
        // Hard-nav so the URL matches the new canonical /u/<newName>.
        window.location.href = `/u/${encodeURIComponent(body.displayName)}`;
        return;
      }
      const body = await r.json().catch(() => ({}));
      const code = String(body.error ?? 'request_failed');
      setRenameError(RENAME_ERRORS[code] ?? code);
      if (code === 'displayName_in_use') {
        setRenameAvail({ kind: 'unavailable', reason: 'in_use' });
      }
    } finally {
      setRenameSubmitting(false);
    }
  }

  return (
    <div className="hm-profile">
      <SharedHeader activeView="profile" />

      <main className="hm-profile-main">
        {loading && <p style={{ color: v('inkMute') }}>Loading…</p>}

        {notFound && (
          <>
            <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 600 }}>User not found</h1>
            <p style={{ color: v('inkMute'), fontSize: 14 }}>
              No Hexponents player goes by "{displayName}".
            </p>
            <p style={{ marginTop: 16 }}>
              <a href="/browse" className="hm-action" style={{ textDecoration: 'none', padding: '8px 16px' }}>
                Browse community puzzles
              </a>
            </p>
          </>
        )}

        {profile && (
          <>
            <header className="hm-profile-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {!renameOpen && <h1 style={{ margin: 0 }}>{profile.displayName}</h1>}
                {/* Owner-only "Edit" button — opens the inline rename form. */}
                {isOwnProfile && !renameOpen && (
                  <button
                    type="button"
                    className="hm-action"
                    onClick={openRename}
                    style={{ padding: '4px 12px', fontSize: 13 }}
                    title="Change your display name"
                  >Edit name</button>
                )}
                {/* Hidden on own profile + when anonymous viewer (server
                    returns null for both flags in those cases). */}
                {profile.viewerIsFollowing != null && !renameOpen && (
                  <button
                    type="button"
                    className="hm-action"
                    onClick={handleFollowToggle}
                    disabled={followBusy}
                    style={{ padding: '4px 12px', fontSize: 13 }}
                  >
                    {profile.viewerIsFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
                {profile.isFollowingViewer && !renameOpen && (
                  <span style={{ fontSize: 11, color: v('inkMute'), background: v('paperDeep'), padding: '2px 6px', borderRadius: 4 }}>
                    Follows you
                  </span>
                )}
              </div>
              {/* Inline rename form — replaces the heading row when open. */}
              {renameOpen && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  padding: 14, borderRadius: 12,
                  background: v('paperDeep'),
                  border: `1px solid ${v('ruleSoft')}`,
                  marginBottom: 4,
                }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12, color: v('inkSoft') }}>New display name</span>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      autoFocus
                      minLength={3}
                      maxLength={30}
                      autoComplete="off"
                      style={{
                        fontFamily: 'inherit', fontSize: 16,
                        padding: '8px 12px', borderRadius: 8,
                        background: v('paper'), color: v('ink'),
                        outline: 'none',
                        border: `1px solid ${
                          renameAvail.kind === 'available' ? v('ok') :
                          renameAvail.kind === 'unavailable' ? v('bad') : v('rule')
                        }`,
                      }}
                    />
                    <span style={{
                      fontSize: 11,
                      color:
                        renameAvail.kind === 'available' ? v('ok') :
                        renameAvail.kind === 'unavailable' ? v('bad') : v('inkMute'),
                    }}>
                      {renameAvail.kind === 'idle' && 'Letters, numbers, underscore, hyphen, dot, space.'}
                      {renameAvail.kind === 'checking' && 'Checking…'}
                      {renameAvail.kind === 'available' && '✓ Available'}
                      {renameAvail.kind === 'unavailable' && (RENAME_REASONS[renameAvail.reason] ?? renameAvail.reason)}
                    </span>
                  </label>
                  {renameError && (
                    <div style={{ fontSize: 12, color: v('bad') }}>{renameError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="hm-primary"
                      onClick={submitRename}
                      disabled={renameSubmitting || renameAvail.kind !== 'available'}
                    >{renameSubmitting ? 'Saving…' : 'Save'}</button>
                    <button
                      type="button"
                      className="hm-action"
                      onClick={() => setRenameOpen(false)}
                      disabled={renameSubmitting}
                    >Cancel</button>
                  </div>
                </div>
              )}
              <p>
                Member since {new Date(profile.memberSince).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </p>
              {(profile.followerCount != null || profile.followingCount != null) && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: v('inkSoft') }}>
                  <span><strong style={{ color: v('ink') }}>{(profile.followerCount ?? 0).toLocaleString()}</strong> {profile.followerCount === 1 ? 'Follower' : 'Followers'}</span>
                  <span><strong style={{ color: v('ink') }}>{(profile.followingCount ?? 0).toLocaleString()}</strong> Following</span>
                </div>
              )}
            </header>

            {profile.stats && (
              <div className="hm-profile-stats" role="group" aria-label="Player stats">
                <Stat label="Current streak" value={profile.stats.currentStreak} unit={profile.stats.currentStreak === 1 ? 'day' : 'days'} />
                <Stat label="Longest streak" value={profile.stats.longestStreak} unit={profile.stats.longestStreak === 1 ? 'day' : 'days'} />
                <Stat label="Daily solves" value={profile.stats.dailySolves} />
                <Stat label="Custom solves" value={profile.stats.customSolves} />
                <Stat label="Custom puzzles created" value={profile.stats.customCreated} />
              </div>
            )}

            {profile.badges && profile.badges.length > 0 && (() => {
              const earned = profile.badges.filter(b => b.earnedAt != null);
              const locked = profile.badges.filter(b => b.earnedAt == null);
              return (
                <>
                  <h2 className="hm-profile-subhead">
                    Badges ({earned.length} / {profile.badges.length})
                  </h2>
                  <div className="hm-profile-badges" role="list" aria-label="Badges">
                    {earned.map(b => {
                      const earnedLine = `Earned ${new Date(b.earnedAt!).toLocaleDateString()}`;
                      return (
                        <div
                          key={b.id}
                          role="listitem"
                          className="hm-profile-badge"
                          title={`${b.description}\n${earnedLine}`}
                        >
                          <div className="hm-profile-badge-icon" aria-hidden="true">{b.icon}</div>
                          <div className="hm-profile-badge-name">{b.name}</div>
                          <div className="hm-profile-badge-date">
                            {new Date(b.earnedAt!).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      );
                    })}
                    {locked.map(b => {
                      // Tooltip lines: badge description, then a
                      // progress hint if the server sent one. Falls
                      // back to "Locked" alone for binary badges that
                      // have no measurable progress.
                      const progressLine = formatBadgeProgress(b.progress);
                      const title = progressLine
                        ? `${b.description}\n${progressLine}`
                        : `${b.description}\nLocked`;
                      return (
                        <div
                          key={b.id}
                          role="listitem"
                          className="hm-profile-badge hm-profile-badge-locked"
                          title={title}
                        >
                          <div className="hm-profile-badge-icon" aria-hidden="true">{b.icon}</div>
                          <div className="hm-profile-badge-name">{b.name}</div>
                          <div className="hm-profile-badge-date">
                            {progressLine ? progressLine.replace('Progress: ', '') : 'Locked'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            <h2 className="hm-profile-subhead">
              Puzzles ({profile.puzzles.length})
            </h2>

            {profile.puzzles.length === 0 ? (
              <p style={{ color: v('inkMute'), fontSize: 14 }}>
                No public puzzles yet.
              </p>
            ) : (
              <ul className="hm-profile-list">
                {profile.puzzles.map(p => (
                  <li key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <a href={`/p/${p.id}`} className="hm-browse-row" style={{ flex: 1 }}>
                      <div className="hm-browse-row-main">
                        <div className="hm-browse-row-name">{p.name}</div>
                        <div className="hm-browse-row-meta">
                          {p.playCount} {p.playCount === 1 ? 'play' : 'plays'}
                          <span className="hm-browse-row-dot">·</span>
                          <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="hm-browse-row-thumbs">
                        <span>👍 {p.thumbsUp}</span>
                        <span>👎 {p.thumbsDown}</span>
                      </div>
                    </a>
                    {isOwnProfile && (
                      <button
                        type="button"
                        className="hm-custom-actbtn hm-custom-actbtn-danger"
                        onClick={() => handleDelete(p.id, p.name)}
                        title="Delete this puzzle"
                      >Delete</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div className="hm-profile-stat">
      <div className="hm-profile-stat-value">{value.toLocaleString()}{unit ? <span className="hm-profile-stat-unit"> {unit}</span> : null}</div>
      <div className="hm-profile-stat-label">{label}</div>
    </div>
  );
}

// Reason → user-facing copy. Keep parallel to the server's
// validateDisplayName() reason codes.
const RENAME_REASONS: Record<string, string> = {
  too_short: 'At least 3 characters.',
  too_long: 'Max 30 characters.',
  invalid_chars: 'Letters, numbers, and underscores only.',
  inappropriate: "That name isn't allowed. Try something else.",
  in_use: 'That name is taken — try another.',
};
const RENAME_ERRORS: Record<string, string> = {
  invalid_displayName: 'That display name was rejected.',
  displayName_in_use: 'Someone just took that name. Try another.',
  rate_limited: 'Too many name changes. Try again later.',
  request_failed: "Couldn't save. Try again in a moment.",
};
